import * as React from 'react';
import { withLocalization, LocalizedComponentProps, LocalizedComponentClass } from '@shared-ui/localization-context';
import { UitkLayoutGrid, UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkButtonSize } from 'uitk-react-button';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { PrimaryButtonTracked } from '../../../utils/click-trackers';
import { useStores } from '../../../utils/hooks';
import { ActivityDetailBasic } from '../../../common/__generated__/api';

export interface LeadTicketBannerProps extends LocalizedComponentProps {
  leadTicket: ActivityDetailBasic.LeadTicketView;
}

export const LeadTicketBanner = withLocalization(({ leadTicket, l10n }: LeadTicketBannerProps) => {
  const { activityDetailStore, routeStore } = useStores();
  const ticketLabel = leadTicket.ticket.label;
  const priceLeadFormatted = leadTicket.ticket.price?.lead.formatted;
  const priceStrikeOutFormatted = leadTicket.ticket.price?.strikeOut?.formatted;
  const likelyToSellOut = leadTicket.ticket.likelyToSellOut?.formatted;
  const ticketMessage = leadTicket.message;
  const buttonLabel = leadTicket.action.text;
  const activityPriceforA11y = priceStrikeOutFormatted
    ? l10n.formatText(
        'lx.activityDetail.originalAndDiscountedPrices.screenReader',
        priceStrikeOutFormatted,
        priceLeadFormatted
      )
    : l10n.formatText('lx.activityDetail.originalPrice.screenReader', priceLeadFormatted);

  if (!priceLeadFormatted || !ticketLabel || !buttonLabel) {
    return null;
  }

  const clickHandler = (): void => {
    const updatedQueryString = routeStore.mergeQueryString(routeStore.search, {
      pwaDialog: 'offer-selection-dialog'
    });
    activityDetailStore.isOfferDialogOpen = true;
    routeStore.updateSearch(updatedQueryString);
  };

  return (
    <>
      <UitkCard padded className="all-y-margin-three all-x-margin-three">
        <UitkCardContentSection data-testid="lead-ticket-banner">
          <UitkLayoutGrid columns={2} alignItems="center">
            <UitkLayoutGridItem className="uitk-price-lockup uitk-type-left all-t-padding-two" colSpan={1}>
              <div id="see-tickets-price-label">
                <span className="uitk-price-a11y is-visually-hidden" aria-hidden={true}>
                  {activityPriceforA11y}
                </span>
                {priceStrikeOutFormatted && (
                  <span className="uitk-price-strikethrough" aria-hidden={true}>
                    {priceStrikeOutFormatted}
                  </span>
                )}
                <span className="uitk-type-heading-600" aria-hidden={true}>
                  {priceLeadFormatted}
                </span>
                <div className="uitk-type-300" aria-hidden={true}>
                  {ticketLabel}
                </div>
                {likelyToSellOut ? (
                  <div
                    data-testid="lead-ticket-banner-ltso"
                    className="uitk-type-200 uitk-cell all-cell-1-1 lx-ltso-urgency"
                  >
                    {likelyToSellOut}
                  </div>
                ) : (
                  ticketMessage && (
                    <div
                      data-testid="lead-ticket-banner-message"
                      className="uitk-type-100 uitk-cell all-cell-1-1 all-t-padding-one vbp-message"
                    >
                      {ticketMessage}
                    </div>
                  )
                )}
              </div>
            </UitkLayoutGridItem>
            <UitkLayoutGridItem className="uitk-type-right" colSpan={1}>
              <UitkLayoutFlex justifyContent="end" id="see-tickets-availability">
                <PrimaryButtonTracked
                  onClick={clickHandler}
                  rfrr="LX.IS.AvailabilityModule.StickyButton"
                  linkName="See tickets button"
                  data-testid="see-tickets-button"
                  text={buttonLabel}
                  size={UitkButtonSize.LARGE}
                />
              </UitkLayoutFlex>
            </UitkLayoutGridItem>
          </UitkLayoutGrid>
        </UitkCardContentSection>
      </UitkCard>
    </>
  );
}) as LocalizedComponentClass<LeadTicketBannerProps>;
